/* ============================================================================
   Furniva wishlist — saved items.

   Guests keep their list in this browser (localStorage). Once someone signs in,
   furniva-account.js merges that guest list into the account Worker's KV list,
   makes the account copy canonical, and mirrors it back here so every heart on
   the storefront keeps showing the same state.

   Everything the page shows about a saved product is re-fetched live from
   Shopify on load (/products/<handle>.js), so a price change or a sell-out is
   reflected rather than remembered from whenever it was saved. Only the handle
   is really stored; the rest is a cache for first paint.
   ============================================================================ */
(function () {
  'use strict';

  var KEY = 'furniva:wishlist:v1';

  function $(s, sc) { return (sc || document).querySelector(s); }
  function $$(s, sc) { return Array.prototype.slice.call((sc || document).querySelectorAll(s)); }
  function on(el, t, h) { if (el) el.addEventListener(t, h); }

  var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root : '/';

  /* ---------------------------------------------------------------- store -- */
  // Storage can throw (private windows, blocked site data). Every access is
  // guarded and the feature degrades to "nothing saved" rather than breaking.
  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }
  function write(list, change) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* not remembered — the UI still reflects this session */
    }
    broadcast(list, change);
  }
  function broadcast(list, change) {
    document.dispatchEvent(new CustomEvent('furniva:wishlist', {
      detail: Object.assign({ items: list }, change || {})
    }));
  }

  function has(handle) {
    return read().some(function (i) { return i.handle === handle; });
  }
  function add(item) {
    var list = read();
    if (list.some(function (i) { return i.handle === item.handle; })) return list;
    list.unshift(item);
    write(list, { action: 'add', handle: item.handle, source: 'storefront' });
    return list;
  }
  function remove(handle) {
    var list = read().filter(function (i) { return i.handle !== handle; });
    write(list, { action: 'remove', handle: handle, source: 'storefront' });
    return list;
  }

  /**
   * Replace the browser mirror with the account Worker's canonical handles.
   * Existing card metadata is retained for first paint; missing metadata is
   * fetched from Shopify by renderPage just like any other saved item.
   */
  function replaceHandles(handles, source) {
    var existing = {};
    read().forEach(function (item) {
      if (item && item.handle) existing[item.handle] = item;
    });
    var seen = {};
    var next = (Array.isArray(handles) ? handles : []).reduce(function (items, value) {
      var handle = String(value || '').trim().toLowerCase();
      if (!/^[a-z0-9][a-z0-9-]{0,119}$/.test(handle) || seen[handle]) return items;
      seen[handle] = true;
      items.push(existing[handle] || { handle: handle, savedAt: Date.now() });
      return items;
    }, []);
    write(next, { action: 'replace', source: source || 'account' });
    return next;
  }

  /* `add` and `remove` are public so the cart can move a line across. Both go
     through write(), so the account sync in furniva-account.js hears the change
     on the same event every other path uses and a signed-in customer's list
     follows. */
  window.FurnivaWishlist = {
    read: read,
    has: has,
    add: add,
    remove: remove,
    replaceHandles: replaceHandles
  };
  document.dispatchEvent(new CustomEvent('furniva:wishlist-ready'));

  /* --------------------------------------------------------------- hearts -- */
  function paint(button, saved) {
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      saved ? 'Remove from saved items' : 'Save to saved items'
    );
    var svg = button.querySelector('svg');
    if (svg) svg.setAttribute('fill', saved ? 'currentColor' : 'none');
  }

  function hapticSuccess() {
    if ('vibrate' in navigator) navigator.vibrate(18);
  }

  function bindHearts(scope) {
    $$('[data-fv-wish]', scope).forEach(function (button) {
      if (button.dataset.fvWishBound) return;
      button.dataset.fvWishBound = '1';
      paint(button, has(button.dataset.fvWish));

      on(button, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var handle = button.dataset.fvWish;
        if (has(handle)) {
          remove(handle);
          paint(button, false);
          hapticSuccess();
        } else {
          add({
            handle: handle,
            title: button.dataset.fvWishTitle || '',
            url: button.dataset.fvWishUrl || root + 'products/' + handle,
            image: button.dataset.fvWishImage || '',
            savedAt: Date.now()
          });
          paint(button, true);
          hapticSuccess();
        }
      });
    });
  }

  /* ---------------------------------------------------------------- count -- */
  function paintCount(list) {
    var n = (list || read()).length;
    $$('[data-fv-wish-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  /* ----------------------------------------------------------------- page -- */
  // Re-fetch each saved product so price and availability are current. A product
  // that 404s has been removed from the catalogue and is dropped from the list.
  function renderPage() {
    var page = $('[data-fv-wishlist-page]');
    if (!page) return;

    var grid = $('[data-fv-wishlist-grid]', page);
    var empty = $('[data-fv-wishlist-empty]', page);
    var countEl = $('[data-fv-wishlist-count]', page);
    var loading = $('[data-fv-wishlist-loading]', page);
    var tpl = $('[data-fv-wishlist-card]');
    if (!grid || !tpl) return;

    var saved = read();

    function finish(items) {
      // Rises in only when it replaces the placeholders, not on a re-render
      // after a remove, where the rest of the grid should stay put.
      var arriving = Boolean(loading && !loading.hidden);
      if (loading) loading.hidden = true;
      grid.innerHTML = '';
      grid.classList.toggle('is-entering', arriving && items.length > 0);
      if (arriving) setTimeout(function () { grid.classList.remove('is-entering'); }, 600);

      if (!items.length) {
        if (empty) empty.hidden = false;
        grid.hidden = true;
        if (countEl) countEl.textContent = 'Nothing saved yet';
        return;
      }

      if (empty) empty.hidden = true;
      grid.hidden = false;
      if (countEl) {
        countEl.textContent = items.length === 1 ? '1 item saved' : items.length + ' items saved';
      }

      items.forEach(function (p) {
        var node = tpl.content.cloneNode(true);
        var card = node.querySelector('[data-card]');
        var img = node.querySelector('[data-image]');
        var titleEl = node.querySelector('[data-title]');
        var priceEl = node.querySelector('[data-price]');
        var compareEl = node.querySelector('[data-compare]');
        var statusEl = node.querySelector('[data-status]');
        var addBtn = node.querySelector('[data-add]');
        var removeBtn = node.querySelector('[data-remove]');
        var linkEls = node.querySelectorAll('[data-link]');

        linkEls.forEach(function (a) { a.href = p.url; });
        if (titleEl) titleEl.textContent = p.title;
        if (img && p.image) {
          img.src = p.image;
          img.alt = p.title;
        } else if (img) {
          img.remove();
        }
        if (priceEl) priceEl.textContent = p.priceText || '';

        if (compareEl) {
          if (p.compareText) compareEl.textContent = p.compareText;
          else compareEl.remove();
        }

        // Availability drives what the card offers, so a saved item never shows
        // an Add to cart it cannot honour.
        if (statusEl) {
          if (p.stale) {
            statusEl.textContent = 'Saved on this device';
            statusEl.className = 'fv-badge fv-badge--neutral fv-badge--sm';
          } else if (p.available) {
            statusEl.textContent = 'In stock';
            statusEl.className = 'fv-badge fv-badge--new fv-badge--sm';
          } else {
            statusEl.textContent = 'Sold out';
            statusEl.className = 'fv-badge fv-badge--soldout fv-badge--sm';
          }
        }

        if (addBtn) {
          if (p.available && p.variantId) {
            addBtn.dataset.variant = p.variantId;
            // The handler needs to know what to take OUT of the saved list.
            addBtn.dataset.handle = p.handle;
          } else {
            addBtn.remove();
          }
        }
        if (removeBtn) removeBtn.dataset.remove = p.handle;
        if (card) card.dataset.handle = p.handle;

        grid.appendChild(node);
      });
    }

    if (!saved.length) { finish([]); return; }
    if (loading) loading.hidden = false;

    Promise.all(
      saved.map(function (item) {
        return fetch(root + 'products/' + item.handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (!data) return null;
            var v = (data.variants || []).filter(function (x) { return x.available; })[0]
              || (data.variants || [])[0];
            return {
              handle: item.handle,
              title: data.title,
              url: root + 'products/' + item.handle,
              image: data.featured_image || item.image,
              available: !!data.available,
              variantId: v && v.id,
              priceText: formatMoney(data.price),
              compareText: data.compare_at_price > data.price ? formatMoney(data.compare_at_price) : null
            };
          })
          .catch(function () {
            // Network failure — show what was cached, labelled honestly rather
            // than asserting a price or stock state we could not confirm.
            return { handle: item.handle, title: item.title, url: item.url, image: item.image, stale: true };
          });
      })
    ).then(function (results) {
      var live = results.filter(Boolean);
      // Drop anything Shopify no longer has.
      var gone = saved.length - live.length;
      if (gone > 0) {
        var kept = saved.filter(function (s) {
          return live.some(function (l) { return l.handle === s.handle; });
        });
        write(kept, {
          action: 'prune',
          handles: saved.filter(function (s) {
            return !live.some(function (l) { return l.handle === s.handle; });
          }).map(function (s) { return s.handle; }),
          source: 'storefront'
        });
      }
      finish(live);
      paintCount();
    });
  }

  // Approved v5.7.2 format: inr = n => '₹' + Number(n).toLocaleString('en-IN').
  // Shopify.formatMoney is deliberately not used — it follows the shop's money
  // format setting, which still renders "Rs. 9,999.00".
  function formatMoney(cents) {
    if (cents == null) return '';
    var rupees = Math.round(cents) / 100;
    var whole = Math.floor(rupees);
    var paise = Math.round((rupees - whole) * 100);
    var out = '₹' + whole.toLocaleString('en-IN');
    if (paise > 0) out += '.' + String(paise).padStart(2, '0');
    return out;
  }

  /* Delegated actions on the wishlist page. */
  on(document, 'click', function (event) {
    var rm = event.target.closest('[data-remove]');
    if (rm) {
      event.preventDefault();
      var gone = rm.closest('[data-card]');
      var leaveCard = gone && window.FurnivaMotion ? window.FurnivaMotion.leave(gone) : Promise.resolve();
      leaveCard.then(function () {
        remove(rm.dataset.remove);
        renderPage();
        if (window.FurnivaToast) window.FurnivaToast.show('Removed from saved items');
      });
      return;
    }
    /* Move, not copy.

       The button used to add to the cart and leave the item saved, so the same
       piece sat in both lists and the saved list slowly became a history of
       everything ever bought. A decision has been made when someone puts it in
       the bag, so the saved entry goes.

       The removal happens only after Shopify has accepted the add. A failed
       add that had already emptied the saved list would lose the item
       altogether. */
    var add = event.target.closest('[data-add][data-variant]');
    if (add) {
      event.preventDefault();
      var handle = add.dataset.handle || '';
      // The same add-to-cart sequence as the rest of the site.
      if (window.FurnivaAddFx) window.FurnivaAddFx.working(add);
      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(add.dataset.variant), quantity: 1 }] })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function (cart) {
          if (window.FurnivaAddFx) window.FurnivaAddFx.added(add, cart);
          else document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (handle) {
            // The card glides out once the add has landed, then the list
            // closes up; a note confirms where it went (owner, 2026-10-10).
            var moved = add.closest('[data-card]');
            var leaveMoved = moved && window.FurnivaMotion ? window.FurnivaMotion.leave(moved) : Promise.resolve();
            leaveMoved.then(function () {
              remove(handle);
              renderPage();
              if (window.FurnivaToast) window.FurnivaToast.show('Moved to cart', { href: '/cart', label: 'View cart' });
            });
          }
        })
        .catch(function () {
          if (window.FurnivaAddFx) window.FurnivaAddFx.failed(add, 'Could not add');
        });
    }
  });

  function init() {
    bindHearts(document);
    paintCount();
    renderPage();
  }

  document.addEventListener('furniva:wishlist', function (e) {
    paintCount(e.detail.items);
    // An account refresh can arrive after this page has rendered. Repaint the
    // cards as well as the count so a stale guest list is never left on screen.
    if (e.detail.source === 'account') renderPage();
  });
  document.addEventListener('furniva:collection:updated', function () { bindHearts(document); });
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
